/**
 * ストア側の Visit 変更を日別データのクエリキャッシュへ反映する
 *
 * シート経由で編集・削除された Visit を、キャッシュ済みの日別データにも
 * 同期させる。キャッシュはビューをまたいで共有されるため、
 * 表示を切り替えても編集結果が保たれる。
 */

import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'

import { generateMovementSessions } from '@/lib/movement-session'
import { useOverlandStore } from '@/store/useOverlandStore'

import { DAY_DATA_QUERY_PREFIX, type DayData } from './day-data'

export const useDayVisitSync = (): void => {
  const queryClient = useQueryClient()
  const storeVisits = useOverlandStore((state) => state.visits)
  const removedVisitIds = useOverlandStore((state) => state.removedVisitIds)
  const clearRemovedVisitIds = useOverlandStore((state) => state.clearRemovedVisitIds)

  // シート経由で更新された Visit をキャッシュに反映
  useEffect(() => {
    if (storeVisits.length === 0) return
    const storeMap = new Map(storeVisits.map((v) => [v.id, v]))
    queryClient.setQueriesData<DayData>({ queryKey: [DAY_DATA_QUERY_PREFIX] }, (data) => {
      if (!data) return data
      let changed = false
      const visits = data.visits.map((v) => {
        const fromStore = storeMap.get(v.id)
        if (!fromStore || fromStore === v) return v
        changed = true
        return fromStore
      })
      // 同じ参照を返すと再レンダリングが起きない
      if (!changed) return data
      return { ...data, visits, movementSessions: generateMovementSessions(data.locations, visits) }
    })
  }, [storeVisits, queryClient])

  // store 側で削除された Visit をキャッシュからも除去
  useEffect(() => {
    if (removedVisitIds.length === 0) return
    // この effect が処理する ID のスナップショットを取り、処理後はその分だけクリア
    // （無条件クリアだと処理中に追加された ID を取りこぼす可能性があるため）
    const idsToConsume = removedVisitIds
    const removedSet = new Set(idsToConsume)
    queryClient.setQueriesData<DayData>({ queryKey: [DAY_DATA_QUERY_PREFIX] }, (data) => {
      if (!data) return data
      const visits = data.visits.filter((v) => !removedSet.has(v.id))
      if (visits.length === data.visits.length) return data
      return { ...data, visits, movementSessions: generateMovementSessions(data.locations, visits) }
    })
    clearRemovedVisitIds(idsToConsume)
  }, [removedVisitIds, clearRemovedVisitIds, queryClient])
}
